import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatLegacyDialog as MatDialog }from '@angular/material/legacy-dialog';
import { AbstractWidgetComponent, ContextChange, DictionaryService, ResponseMessage, ResponseStatus, UserProfileDialogService, WidgetStyle } from '@sdbn/appclient';
import { MasterDetailListEntry, TranslationService } from '@sdbn/common';
import { Subscription } from 'rxjs';
import { ConfirmDialogComponent, ConfirmDialogData } from 'src/app/shared/confirm-dialog/confirm.dialog.component';
import { ProcessingStatusModelService } from '../../processing-status-model.service';
import { ProcessingStatus, ProcessingStatusModel, ProcessingStatusModelDetails } from '../../processing-status-models-model';

@Component({
  selector: 'app-processing-status-models',
  templateUrl: './processing-status-models.component.html',
  styleUrls: ['./processing-status-models.component.scss']
})
export class ProcessingStatusModelsComponent extends AbstractWidgetComponent implements OnInit, OnDestroy {

  newStatusId = -1;

  models: ProcessingStatusModel[] = [];

  selectedModel: ProcessingStatusModel;
  selectedDetails: ProcessingStatusModelDetails = new ProcessingStatusModelDetails();
  workingCopyDetails: ProcessingStatusModelDetails = new ProcessingStatusModelDetails();

  responseMessages: ResponseMessage[];
  isLoading = false;

  @ViewChild('statusModelForm') form: NgForm;

  languageSubscription: Subscription;

  get cancelEnabled() {
    return !this.isLoading && this.form != null && this.form.dirty;
  }

  get saveEnabled() {
    return !this.isLoading && this.form != null && this.form.valid && this.form.dirty && this.workingCopyDetails.name?.checkValueExistsForLanguage('de');
  }

  constructor(
    public translationService: TranslationService,
    private processingStatusModelService: ProcessingStatusModelService,
    private dialog: MatDialog,
    private dictionaryService: DictionaryService,
    public userProfileDialogService: UserProfileDialogService) {

    super();

    this.languageSubscription = this.translationService.languageSubject.subscribe(language => {

      this.createTranslatedValuesOnList();

    });

  }

  createTranslatedValuesOnList() {
    for (const model of this.models) {
      model.translatedLabel = this.translationService.translate(model.nameDictionaryKey);
    }
  }

  ngOnInit() {
    this.load();
  }

  ngOnDestroy(): void {

    super.ngOnDestroy();

    if (this.languageSubscription != null) {
      this.languageSubscription.unsubscribe();
    }
  }

  setSelected(model: ProcessingStatusModel) {

    if (this.selectedModel != null && this.selectedModel.id === model.id) {
      return;
    }

    if (this.form?.form?.dirty) {
      const dialogData: ConfirmDialogData = {
        titleKey: 'tmc-yard.widgets.processing-status-models.discard.confirm-dialog.title',
        messageKey: 'tmc-yard.widgets.processing-status-models.discard.confirm-dialog.message',
        confirm: () => {
          this.setSelectedWithoutCheck(model);
        }
      };
      this.dialog.open(ConfirmDialogComponent, {
        data: dialogData
      });
    } else {
      this.setSelectedWithoutCheck(model);
    }
  }

  setSelectedWithoutCheck(model: ProcessingStatusModel) {
    this.isLoading = true;
    this.responseMessages = [];
    if (model != null) {
      this.processingStatusModelService.getProcessingStatusModelDetails(model.id).subscribe(details => {
        this.selectedModel = model;
        this.selectedDetails = new ProcessingStatusModelDetails(details);
        this.workingCopyDetails = new ProcessingStatusModelDetails(details);
        this.form.resetForm(this.workingCopyDetails);
        this.isLoading = false;
      }, error => {
        this.isLoading = false;
        console.log(error);
      });
    } else {
      this.isLoading = false;
    }
  }

  isSelected(details: ProcessingStatusModelDetails) {
    return this.selectedDetails != null && this.selectedDetails.id === details.id;
  }

  delete(model: ProcessingStatusModel) {

    this.responseMessages = [];

    const dialogData: ConfirmDialogData = {
      titleKey: 'tmc-yard.widgets.processing-status-models.delete.confirm-dialog.title',
      messageKey: 'tmc-yard.widgets.processing-status-models.delete.confirm-dialog.message',
      confirm: () => {
        this.isLoading = true;
        this.processingStatusModelService.delete(model.id).subscribe(response => {
          if (response.status === ResponseStatus.SUCCESS) {
            this.clearAndLoad();
          } else {
            this.responseMessages = response.responseMessages;
          }
          this.isLoading = false;
        }, error => {
          console.log(error);
          this.isLoading = false;
        });
      }
    };

    this.dialog.open(ConfirmDialogComponent, {
      data: dialogData
    });
  }

  load() {
    this.isLoading = true;
    this.responseMessages = [];
    this.processingStatusModelService.getProcessingStatusModels().subscribe(models => {

      const newModels = new Array<ProcessingStatusModel>();
      for (const model of models) {
        newModels.push(new ProcessingStatusModel(model));
      }
      this.models = newModels;

      if (this.models.length > 0) {
        if (this.selectedModel == null) {
          this.setSelectedWithoutCheck(this.models[0]);
        } else {
          this.setSelectedWithoutCheck(this.models.find(s => s.id === this.selectedModel.id));
        }
      }

      this.createTranslatedValuesOnList();

      this.isLoading = false;
    }, error => {
      this.models = [];
      console.error(error);
      this.isLoading = false;
    });
  }

  addNew() {
    if (this.form.form.dirty) {
      const dialogData: ConfirmDialogData = {
        titleKey: 'tmc-yard.widgets.processing-status-models.discard.confirm-dialog.title',
        messageKey: 'tmc-yard.widgets.processing-status-models.discard.confirm-dialog.message',
        confirm: () => {
          this.clear();
        }
      };
      this.dialog.open(ConfirmDialogComponent, {
        data: dialogData
      });
    } else {
      this.clear();
    }
  }

  cancel() {
    if (this.form.form.dirty) {
      const dialogData: ConfirmDialogData = {
        titleKey: 'tmc-yard.widgets.processing-status-models.discard.confirm-dialog.title',
        messageKey: 'tmc-yard.widgets.processing-status-models.discard.confirm-dialog.message',
        confirm: () => {
          this.discardChanges();
        }
      };
      this.dialog.open(ConfirmDialogComponent, {
        data: dialogData
      });
    } else {
      this.discardChanges();
    }
  }

  discardChanges() {
    this.responseMessages = [];
    this.form.resetForm(this.form.value);
    if (this.workingCopyDetails.id != null) {
      this.setSelectedWithoutCheck(this.selectedModel);
    } else {
      this.workingCopyDetails = new ProcessingStatusModelDetails();
      this.selectedModel = null;
    }
  }

  clear() {
    this.form.resetForm(this.form.value);
    this.responseMessages = [];
    this.selectedModel = null;
    this.selectedDetails = new ProcessingStatusModelDetails();
    this.workingCopyDetails = new ProcessingStatusModelDetails(this.selectedDetails);
  }

  clearAndLoad() {
    this.clear();
    this.load();
  }

  save() {
    this.processingStatusModelService.save(this.workingCopyDetails).subscribe(response => {
      this.isLoading = false;
      if (response.status === ResponseStatus.SUCCESS) {
        this.form.resetForm(this.form.value);
        this.selectedDetails = new ProcessingStatusModelDetails(this.workingCopyDetails);
        this.dictionaryService.initializeDataDictionary().subscribe(() => {
          this.load();
        });
      } else {
        this.responseMessages = response.responseMessages;
      }
    }, error => {
      console.log(error);
      this.isLoading = false;
    });
  }

  public onDictionaryChanged() {
    this.form.form.markAsDirty();
  }


  public addStatus() {
    this.workingCopyDetails.processingStatus.push(new ProcessingStatus({ id: this.newStatusId-- }));
    this.updateStatusIndex();
    this.form.form.markAsDirty();
  }

  public removeStatus(status: ProcessingStatus) {
    if (status.id > 0) {
      status.deleted = true;
    } else {
      const index = this.workingCopyDetails.processingStatus.indexOf(status);
      this.workingCopyDetails.processingStatus.splice(index, 1);
    }

    this.updateStatusIndex();
    this.form.form.markAsDirty();
  }

  public drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.workingCopyDetails.processingStatus, event.previousIndex, event.currentIndex);
    this.updateStatusIndex();
    this.form.form.markAsDirty();
  }

  public colorPickerChanged(event: any) {
    this.form.form.markAsDirty();
  }

  private updateStatusIndex() {
    let index = 0;
    for (const status of this.workingCopyDetails.processingStatus) {
      status.index = index++;
    }
  }

  onContextChange(contextChange: ContextChange) {
  }

  onSetElementAndContext() {
  }

  onSelectionChanged(entry: MasterDetailListEntry) {
    this.setSelected(entry as ProcessingStatusModel);
  }

  onAddEntry(parentPath: Array<MasterDetailListEntry>) {
    this.addNew();
  }

  onDeleteEntry(entry: MasterDetailListEntry) {
    this.delete(entry as ProcessingStatusModel);
  }

  getStyle(): WidgetStyle {
    return WidgetStyle.transparent;
  }

}
